import { PDFDocument, StandardFonts } from "pdf-lib"

import { monto } from "@/componentes/tesoreria/dinero"
import { comoFecha, comoFechaHora } from "@/lib/fechas"

import {
  ALTO,
  ANCHO,
  enMonedas,
  escribible,
  GRIS,
  LINEA,
  type Lienzo,
  lineas,
  MARGEN,
  membrete,
  nuevaPagina,
  ROJO,
  texto,
  UTIL,
} from "./pdfReporte"
import { nombreDelPago, type Semana } from "./tiposSemana"

/**
 * El PDF de los pagos programados de la semana, compuesto en el navegador (13/10).
 *
 * El mismo papel, tinta y membrete que el reporte de cuentas por pagar
 * (`pdfReporte.ts`), y por la misma razón: `window.print()` no hace nada en el
 * navegador de Telegram, que es desde donde se abre. Un bloque por día —los días sin
 * pagos no ocupan papel— y una tabla: pago, a quién, monto y en qué paso está.
 */

const COLUMNAS = [
  { rotulo: "Pago", ancho: 0 },
  { rotulo: "A quién", ancho: 190 },
  { rotulo: "Situación", ancho: 170 },
  { rotulo: "Monto", ancho: 96, derecha: true },
]
const PAGO = UTIL - COLUMNAS.reduce((s, c) => s + c.ancho, 0) - 3 * 6
const ANCHOS = COLUMNAS.map((c) => (c.rotulo === "Pago" ? PAGO : c.ancho))

function cabecera(l: Lienzo) {
  let x = MARGEN
  COLUMNAS.forEach((c, i) => {
    texto(l, c.rotulo, x, l.y, {
      cuerpo: 7.5,
      fuente: l.negrita,
      color: GRIS,
      derecha: c.derecha ? ANCHOS[i] : undefined,
    })
    x += (ANCHOS[i] ?? 0) + 6
  })
  l.y -= 5
  l.pagina.drawLine({
    start: { x: MARGEN, y: l.y },
    end: { x: ANCHO - MARGEN, y: l.y },
    thickness: 0.5,
    color: LINEA,
  })
  l.y -= 11
}

/** Compone el PDF. `logo` son los bytes de un PNG; sin él, el membrete va sin marca. */
export async function pdfDeLaSemana(
  s: Semana,
  logo: Uint8Array | null,
): Promise<Uint8Array<ArrayBuffer>> {
  const pdf = await PDFDocument.create()
  pdf.setTitle(s.titulo)
  const l: Lienzo = {
    pdf,
    pagina: pdf.addPage([ANCHO, ALTO]),
    y: ALTO - MARGEN,
    normal: await pdf.embedFont(StandardFonts.Helvetica),
    negrita: await pdf.embedFont(StandardFonts.HelveticaBold),
  }
  membrete(l, s, logo ? await pdf.embedPng(logo) : null)
  texto(l, s.titulo, MARGEN, l.y, { cuerpo: 16, fuente: l.negrita })
  l.y -= 15
  texto(l, `Generado el ${comoFechaHora(s.generado_en)}`, MARGEN, l.y, { color: GRIS })
  l.y -= 20
  texto(
    l,
    [
      `Pagos: ${s.total_pagos} · ${enMonedas(s.total)}`,
      `Gastos: ${enMonedas(s.gastos)}`,
      `Nómina: ${enMonedas(s.nomina)}`,
    ].join("     "),
    MARGEN,
    l.y,
    { cuerpo: 9.5, fuente: l.negrita },
  )
  l.y -= 15
  texto(
    l,
    `Por generar: ${s.por_generar}${s.por_generar > 0 ? ` · ${enMonedas(s.saldos_por_generar)}` : ""}`,
    MARGEN,
    l.y,
    { cuerpo: 9, color: s.por_generar > 0 ? ROJO : GRIS },
  )
  l.y -= 26

  if (s.total_pagos === 0)
    texto(l, "No hay pagos programados esta semana.", MARGEN, l.y, { color: GRIS })

  for (const dia of s.dias.filter((d) => d.pagos.length > 0)) {
    if (l.y < MARGEN + 70) nuevaPagina(l)
    const encabezar = (sigue: boolean) => {
      texto(l, `${dia.nombre} ${comoFecha(dia.fecha)}${sigue ? " (continúa)" : ""}`, MARGEN, l.y, {
        cuerpo: 10.5,
        fuente: l.negrita,
      })
      texto(l, enMonedas(dia.total), MARGEN, l.y, {
        cuerpo: 10.5,
        fuente: l.negrita,
        derecha: UTIL,
      })
      l.y -= 16
      cabecera(l)
    }
    encabezar(false)
    for (const p of dia.pagos) {
      const nombre = lineas(l.normal, 8.5, nombreDelPago(p), PAGO)
      const alto = nombre.length * 10.5 + 6
      if (l.y - alto < MARGEN + 14) {
        nuevaPagina(l)
        encabezar(true)
      }
      let x = MARGEN
      nombre.forEach((ln, k) => {
        texto(l, ln, x, l.y - k * 10.5)
      })
      x += PAGO + 6
      texto(l, escribible(l.normal, p.tercero ?? "—"), x, l.y)
      x += (ANCHOS[1] ?? 0) + 6
      texto(l, p.situacion, x, l.y, {
        color: p.encaminado ? GRIS : ROJO,
        fuente: p.encaminado ? l.normal : l.negrita,
      })
      x += (ANCHOS[2] ?? 0) + 6
      texto(l, `${p.divisa} ${monto(p.monto)}`, x, l.y, {
        fuente: l.negrita,
        derecha: ANCHOS[3],
      })
      l.y -= alto
      l.pagina.drawLine({
        start: { x: MARGEN, y: l.y + 8 },
        end: { x: ANCHO - MARGEN, y: l.y + 8 },
        thickness: 0.4,
        color: LINEA,
      })
    }
    l.y -= 14
  }

  const paginas = pdf.getPages()
  paginas.forEach((p, i) => {
    const pie = escribible(
      l.normal,
      `${s.empresa_nombre} · ${s.titulo} · página ${i + 1} de ${paginas.length}`,
    )
    p.drawText(pie, {
      x: ANCHO - MARGEN - l.normal.widthOfTextAtSize(pie, 7),
      y: MARGEN / 2,
      size: 7,
      font: l.normal,
      color: GRIS,
    })
  })
  return new Uint8Array(await pdf.save())
}
